/**
 * Click or drag-to-size placement for `canvasShape` nodes (toolbar shapes).
 * Pairs with `useCanvasShapeAndEdgeStyle` for stroke/fill.
 */

import { Node, useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CANVAS_SNAP } from "../canvas-controls.config";
import { useCanvas } from "../context/CanvasContext";
import type { ShapeType } from "../nodes/shapes/index";
import { SHAPE_COLORS } from "../nodes/shared/CanvasColorPalettes";
import { normalizeCanvasStrokeWidth } from "../types/canvasNodeData";
import { attachNodeToSectionIfApplicable } from "../utils/sectionMembership";
import type { ShapeStrokeStyle } from "./useCanvasShapeAndEdgeStyle";

const snapToGrid = (val: number, step: number) => Math.round(val / step) * step;

/** Minimum drag distance (screen px) before we treat the gesture as drag-to-size. */
const MIN_DRAG_PX = 40;

/** Default dimensions when the user single-clicks (no significant drag). */
const DEFAULT_SIZE = 150;
const MIN_SIZE = 40;

export interface ShapePlacementGhost {
  position: { x: number; y: number };
  width: number;
  height: number;
}

interface UseShapePlacementOptions {
  activeShapeColor: string;
  activeShapeNoFill: boolean;
  activeShapeStrokeStyle: ShapeStrokeStyle;
  activeShapeStrokeWidth: number;
  setLastPlacedShape: (shape: ShapeType) => void;
}

export function useShapePlacement({
  activeShapeColor,
  activeShapeNoFill,
  activeShapeStrokeStyle,
  activeShapeStrokeWidth,
  setLastPlacedShape,
}: UseShapePlacementOptions) {
  const reactFlowInstance = useReactFlow();
  const { screenToFlowPosition } = reactFlowInstance;
  const { addNode } = useCanvas();
  const activeShapeStrokeColor =
    SHAPE_COLORS.find((c) => c.value === activeShapeColor)?.swatchHex ??
    activeShapeColor;

  const [isPlacingShape, setIsPlacingShape] = useState(false);
  const [pendingShapeType, setPendingShapeType] = useState<ShapeType | null>(
    null,
  );
  const [ghost, setGhost] = useState<ShapePlacementGhost | null>(null);

  // Refs so window event handlers always read the latest style/placement values.
  const screenToFlowRef = useRef(screenToFlowPosition);
  screenToFlowRef.current = screenToFlowPosition;
  const reactFlowInstanceRef = useRef(reactFlowInstance);
  reactFlowInstanceRef.current = reactFlowInstance;
  const pendingShapeTypeRef = useRef(pendingShapeType);
  pendingShapeTypeRef.current = pendingShapeType;
  const activeColorRef = useRef(activeShapeColor);
  activeColorRef.current = activeShapeColor;
  const activeStrokeColorRef = useRef(activeShapeStrokeColor);
  activeStrokeColorRef.current = activeShapeStrokeColor;
  const activeNoFillRef = useRef(activeShapeNoFill);
  activeNoFillRef.current = activeShapeNoFill;
  const activeStrokeStyleRef = useRef(activeShapeStrokeStyle);
  activeStrokeStyleRef.current = activeShapeStrokeStyle;
  const activeStrokeWidthRef = useRef(activeShapeStrokeWidth);
  activeStrokeWidthRef.current = activeShapeStrokeWidth;
  const setLastPlacedShapeRef = useRef(setLastPlacedShape);
  setLastPlacedShapeRef.current = setLastPlacedShape;

  const [snapX, snapY] = CANVAS_SNAP.grid;

  const startPlacing = useCallback((shapeType: ShapeType) => {
    setPendingShapeType(shapeType);
    setIsPlacingShape(true);
  }, []);

  const cancelPlacing = useCallback(() => {
    setIsPlacingShape(false);
    setPendingShapeType(null);
    setGhost(null);
  }, []);

  useEffect(() => {
    if (!isPlacingShape) {
      setGhost(null);
      return;
    }

    let dragStart: { x: number; y: number } | null = null;
    let dragStartScreen: { x: number; y: number } | null = null;

    const getFlow = (e: MouseEvent | PointerEvent) =>
      screenToFlowRef.current({ x: e.clientX, y: e.clientY });

    const isOnPane = (e: MouseEvent | PointerEvent): boolean => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-canvas-control]")) return false;
      return !!target.closest(".react-flow__pane");
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        cancelPlacing();
      }
    };

    // Block React Flow's pointer-based drag-to-select while we own the gesture.
    // RF uses pointerdown (not mousedown) to start its selection rectangle — stopping
    // propagation here in capture prevents the selection box from ever appearing.
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 || !isOnPane(e)) return;
      e.stopPropagation();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (e.button !== 0 || !isOnPane(e)) return;
      e.stopPropagation();
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0 || !isOnPane(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragStartScreen = { x: e.clientX, y: e.clientY };
      const pos = getFlow(e);
      dragStart = {
        x: snapToGrid(pos.x, snapX),
        y: snapToGrid(pos.y, snapY),
      };
    };

    const onMouseMove = (e: MouseEvent) => {
      const pos = getFlow(e);

      if (dragStart && dragStartScreen && e.buttons === 1) {
        const dx = e.clientX - dragStartScreen.x;
        const dy = e.clientY - dragStartScreen.y;
        if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;

        const x = snapToGrid(
          Math.min(dragStart.x, snapToGrid(pos.x, snapX)),
          snapX,
        );
        const y = snapToGrid(
          Math.min(dragStart.y, snapToGrid(pos.y, snapY)),
          snapY,
        );
        const width = Math.max(
          snapX,
          snapToGrid(Math.abs(pos.x - dragStart.x), snapX),
        );
        const height = Math.max(
          snapY,
          snapToGrid(Math.abs(pos.y - dragStart.y), snapY),
        );
        setGhost({ position: { x, y }, width, height });
      } else {
        // Hover ghost — top-left corner tracks the cursor exactly,
        // matching where the node will be placed on click.
        setGhost({
          position: {
            x: snapToGrid(pos.x, snapX),
            y: snapToGrid(pos.y, snapY),
          },
          width: DEFAULT_SIZE,
          height: DEFAULT_SIZE,
        });
      }
    };

    const onMouseUp = (e: MouseEvent) => {
      if (e.button !== 0 || !dragStart || !dragStartScreen) {
        dragStart = null;
        dragStartScreen = null;
        return;
      }

      const shapeType = pendingShapeTypeRef.current;
      if (!shapeType) {
        dragStart = null;
        dragStartScreen = null;
        return;
      }

      const dx = e.clientX - dragStartScreen.x;
      const dy = e.clientY - dragStartScreen.y;
      const isDrag = Math.abs(dx) >= MIN_DRAG_PX || Math.abs(dy) >= MIN_DRAG_PX;

      let position: { x: number; y: number };
      let width: number;
      let height: number;

      if (isDrag) {
        const pos = getFlow(e);
        position = {
          x: snapToGrid(Math.min(dragStart.x, pos.x), snapX),
          y: snapToGrid(Math.min(dragStart.y, pos.y), snapY),
        };
        width = Math.max(
          MIN_SIZE,
          snapToGrid(Math.abs(pos.x - dragStart.x), snapX),
        );
        height = Math.max(
          MIN_SIZE,
          snapToGrid(Math.abs(pos.y - dragStart.y), snapY),
        );
      } else {
        position = { x: dragStart.x, y: dragStart.y };
        width = DEFAULT_SIZE;
        height = DEFAULT_SIZE;
      }

      const newNode: Node = {
        id: uuidv4(),
        type: "canvasShape",
        position,
        width,
        height,
        data: {
          shape_type: shapeType,
          fill_color: activeColorRef.current,
          stroke_color: activeStrokeColorRef.current,
          no_fill: activeNoFillRef.current,
          stroke_style: activeStrokeStyleRef.current,
          stroke_width: normalizeCanvasStrokeWidth(
            activeStrokeWidthRef.current,
          ),
          content: "",
        },
      };

      // Auto-parent into the section under the placement rect (if any).
      const rf = reactFlowInstanceRef.current;
      const attached = attachNodeToSectionIfApplicable({
        node: newNode,
        nodes: rf.getNodes(),
        getInternalNode: (id) => rf.getInternalNode(id),
      });

      addNode({ ...attached, selected: true });
      setLastPlacedShapeRef.current(shapeType);

      dragStart = null;
      dragStartScreen = null;
      setIsPlacingShape(false);
      setPendingShapeType(null);
      setGhost(null);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    window.addEventListener("pointerup", onPointerUp, { capture: true });
    window.addEventListener("mousedown", onMouseDown, { capture: true });
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp, { capture: true });

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown, {
        capture: true,
      });
      window.removeEventListener("pointerup", onPointerUp, { capture: true });
      window.removeEventListener("mousedown", onMouseDown, { capture: true });
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp, { capture: true });
    };
  }, [isPlacingShape, cancelPlacing, snapX, snapY, addNode, reactFlowInstance]);

  return {
    isPlacingShape,
    pendingShapeType,
    ghost,
    startPlacing,
    cancelPlacing,
  };
}
